import React, { useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Link,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

interface SignatureDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (signature: {
    type: 'draw' | 'type';
    value: string;
    typedText?: string;
  }) => void;
  initialSignature?: {
    type: 'draw' | 'type';
    value: string;
    typedText?: string;
  } | null;
}

const SignatureDialog: React.FC<SignatureDialogProps> = ({
  open,
  onClose,
  onCreate,
  initialSignature,
}) => {
  const [tab, setTab] = useState<'draw' | 'type' | 'upload'>('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [drawnSignature, setDrawnSignature] = useState('');
  const [uploadedSignature, setUploadedSignature] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    }
  };
  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      ctx.stroke();
    }
  };
  const handleCanvasMouseUp = () => {
    setDrawing(false);
    if (canvasRef.current) {
      setDrawnSignature(canvasRef.current.toDataURL('image/png'));
      setUndoStack((stack) => [
        ...stack,
        canvasRef.current!.toDataURL('image/png'),
      ]);
      setRedoStack([]);
    }
  };
  const handleClearCanvas = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setDrawnSignature('');
      setUndoStack([]);
      setRedoStack([]);
    }
  };
  const handleUndo = () => {
    if (undoStack.length > 1) {
      const newUndo = [...undoStack];
      const last = newUndo.pop();
      setRedoStack((stack) => [last!, ...stack]);
      const prev = newUndo[newUndo.length - 1];
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx && canvasRef.current && prev) {
        const img = new window.Image();
        img.onload = () => {
          ctx.clearRect(
            0,
            0,
            canvasRef.current!.width,
            canvasRef.current!.height
          );
          ctx.drawImage(img, 0, 0);
        };
        img.src = prev;
        setDrawnSignature(prev);
      }
      setUndoStack(newUndo);
    }
  };
  const handleRedo = () => {
    if (redoStack.length > 0) {
      const [next, ...rest] = redoStack;
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx && canvasRef.current && next) {
        const img = new window.Image();
        img.onload = () => {
          ctx.clearRect(
            0,
            0,
            canvasRef.current!.width,
            canvasRef.current!.height
          );
          ctx.drawImage(img, 0, 0);
        };
        img.src = next;
        setDrawnSignature(next);
      }
      setUndoStack((stack) => [...stack, next]);
      setRedoStack(rest);
    }
  };
  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setUploadedSignature(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreate = () => {
    if (tab === 'draw' && drawnSignature) {
      onCreate({ type: 'draw', value: drawnSignature });
      onClose();
    } else if (tab === 'type' && typedSignature) {
      // Convert typed signature to image data URL
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = 300;
        canvas.height = 80;

        // Set background to transparent
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Set font style for signature
        ctx.font = '32px cursive';
        ctx.fillStyle = '#1976d2';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Draw the typed signature
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

        // Convert to data URL
        const signatureImageUrl = canvas.toDataURL('image/png');
        onCreate({
          type: 'draw',
          value: signatureImageUrl,
          typedText: typedSignature,
        });
        onClose();
      }
    } else if (tab === 'upload' && uploadedSignature) {
      onCreate({ type: 'draw', value: uploadedSignature });
      onClose();
    }
  };

  // When dialog opens, pre-fill with initialSignature
  React.useEffect(() => {
    if (open && initialSignature) {
      if (
        initialSignature.type === 'draw' &&
        initialSignature.value.startsWith('data:image/')
      ) {
        setTab('draw');
        setDrawnSignature(initialSignature.value);
        setTypedSignature('');
        setUploadedSignature('');

        // If this was a typed signature, restore the typed text
        if (initialSignature.typedText) {
          setTypedSignature(initialSignature.typedText);
        }
      } else if (initialSignature.type === 'type') {
        setTab('type');
        setTypedSignature(initialSignature.value);
        setDrawnSignature('');
        setUploadedSignature('');
      }
    } else if (open) {
      setTab('draw');
      setTypedSignature('');
      setDrawnSignature('');
      setUploadedSignature('');
    }
  }, [open, initialSignature]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 0, background: '#f7fafd', borderRadius: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            px: 3,
            pt: 2,
          }}
        >
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 0 }}>
            <Tab
              label={
                <>
                  <span style={{ fontWeight: 600 }}>✍️</span> Draw
                </>
              }
              value="draw"
              sx={{ fontWeight: 700, fontSize: 18, minWidth: 120 }}
            />
            <Tab
              label={
                <>
                  <span style={{ fontWeight: 600 }}>T</span> Type
                </>
              }
              value="type"
              sx={{ fontWeight: 700, fontSize: 18, minWidth: 120 }}
            />
            <Tab
              label={
                <>
                  <CloudUploadIcon sx={{ fontSize: 20, mb: '-2px', mr: 1 }} />
                  Upload
                </>
              }
              value="upload"
              sx={{ fontWeight: 700, fontSize: 18, minWidth: 120 }}
            />
          </Tabs>
        </Box>
        <DialogContent sx={{ background: '#f7fafd', pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
            <Box
              sx={{
                width: 60,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mr: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  minWidth: 0,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#fff',
                  color: '#222',
                  mb: 1,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}
              >
                <ArrowDropDownIcon />
              </Button>
              <Box
                sx={{
                  background: '#ffe082',
                  borderRadius: '0 0 6px 6px',
                  px: 1.5,
                  py: 0.5,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    color: '#222',
                    fontWeight: 700,
                    fontSize: 16,
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    letterSpacing: 1,
                  }}
                >
                  sign
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                background: '#fff',
                borderRadius: 2,
                minHeight: 160,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px solid #e0e0e0',
                position: 'relative',
                p: 2,
              }}
            >
              {tab === 'draw' && (
                <>
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={120}
                    style={{
                      border: 'none',
                      background: '#fafbfc',
                      borderRadius: 8,
                      cursor: 'crosshair',
                      width: '100%',
                      height: 120,
                      marginBottom: 8,
                    }}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseMove={handleCanvasMouseMove}
                    onMouseUp={handleCanvasMouseUp}
                    onMouseLeave={handleCanvasMouseUp}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={handleUndo}
                      disabled={undoStack.length <= 1}
                      sx={{ minWidth: 0, p: 0.5 }}
                    >
                      <UndoIcon />
                    </Button>
                    <Button
                      onClick={handleRedo}
                      disabled={redoStack.length === 0}
                      sx={{ minWidth: 0, p: 0.5 }}
                    >
                      <RedoIcon />
                    </Button>
                    <Button
                      onClick={handleClearCanvas}
                      sx={{ minWidth: 0, p: 0.5 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </>
              )}
              {tab === 'type' && (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                  }}
                >
                  <TextField
                    label="Type your signature here"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                      right: 16,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'cursive',
                      fontSize: 28,
                      color: '#1976d2',
                      border: '1px dashed #ccc',
                      borderRadius: 2,
                      px: 2,
                      py: 0.25,
                      minHeight: 30,
                      width: 'calc(100% - 32px)',
                      textAlign: 'center',
                      background: '#fafbfc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'absolute',
                      top: 16,
                      left: 16,
                    }}
                  >
                    {typedSignature || 'Your signature preview'}
                  </Typography>
                </Box>
              )}
              {tab === 'upload' && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 120,
                  }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Upload Image
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleUpload}
                    />
                  </Button>
                  {uploadedSignature && (
                    <img
                      src={uploadedSignature}
                      alt="Uploaded signature"
                      style={{
                        maxWidth: 320,
                        maxHeight: 80,
                        border: '1px dashed #ccc',
                        borderRadius: 4,
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Link
              href="https://www.adobe.com/sign/legal.html"
              target="_blank"
              rel="noopener"
              sx={{ fontSize: 15, color: '#1976d2', fontWeight: 500 }}
            >
              What makes a signature legal?
            </Link>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={onClose}
                sx={{
                  borderRadius: 6,
                  px: 3,
                  fontWeight: 600,
                  color: '#222',
                  background: '#fff',
                  border: '1.5px solid #e0e0e0',
                  mr: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  borderRadius: 6,
                  px: 3,
                  fontWeight: 700,
                  fontSize: 17,
                  background: '#222',
                  color: '#fff',
                  boxShadow: 'none',
                }}
                onClick={handleCreate}
                disabled={
                  (tab === 'draw' && !drawnSignature) ||
                  (tab === 'type' && !typedSignature) ||
                  (tab === 'upload' && !uploadedSignature)
                }
              >
                Create Signature
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default SignatureDialog;
